"use client";

import HubSpotForm from "@/components/interactive/HubSpotForm";
import EventBriteForm from "@/components/interactive/EventBriteForm";
import SplashForm from "@/components/interactive/SplashForm";
import ErrorBoundary from "@/components/ErrorBoundary";
import { detectFormType } from "@/utils/detectFormType";
import type { Dictionary } from "@/dictionaries";

interface FormRendererProps {
  formId: string;
  minHeight?: number;
  webexMeetingId?: string;
  redirectUrl?: string;
  thankYouMessage?: any;
  locale?: string;
  eventDates?: string;
  onFormSubmit?: (data: any) => void;
  onOrderComplete?: (orderData: any) => void;
  onFormReady?: () => void;
  theme?: string;
  hasRedirect?: boolean;
  dict?: Dictionary;
  hiddenFields?: Record<string, string>;
}

function FormRendererImpl({
  formId,
  minHeight,
  webexMeetingId,
  redirectUrl,
  thankYouMessage,
  locale,
  eventDates,
  onFormSubmit,
  onOrderComplete,
  onFormReady,
  theme,
  hasRedirect = false,
  dict,
  hiddenFields,
}: FormRendererProps) {
  const formType = detectFormType(formId);

  if (formType === "hubspot") {
    return (
      <HubSpotForm
        formId={formId}
        webexMeetingId={webexMeetingId}
        redirectUrl={redirectUrl}
        thankYouMessage={thankYouMessage}
        locale={locale}
        eventDates={eventDates}
        minHeight={minHeight}
        onFormSubmit={onFormSubmit}
        onFormReady={onFormReady}
        hasRedirect={hasRedirect}
        theme={theme}
        hiddenFields={hiddenFields}
      />
    );
  }

  if (formType === "eventbrite") {
    return (
      <EventBriteForm
        eventId={formId}
        onOrderComplete={onOrderComplete}
        onWidgetReady={onFormReady}
        theme={theme}
      />
    );
  }

  if (formType === "splash") {
    return (
      <SplashForm
        eventId={formId}
        minHeight={minHeight}
        theme={theme}
        unavailableLabel={
          dict?.ui?.form?.unavailable ?? "Form currently unavailable."
        }
      />
    );
  }

  return <p>{dict?.ui?.form?.unavailable ?? "Form currently unavailable."}</p>;
}

export default function FormRenderer(props: FormRendererProps) {
  const unavailableLabel =
    props.dict?.ui?.form?.unavailable ?? "Form currently unavailable.";

  return (
    <ErrorBoundary fallback={<p>{unavailableLabel}</p>}>
      <FormRendererImpl {...props} />
    </ErrorBoundary>
  );
}
